/**
 * Resumable onboarding AREA — typed API client (PR-ONB-RESUME-1B).
 *
 * Mirrors the conventions of `lib/api.ts` (vanilla fetch, discriminated
 * result unions, safe JSON parsing). Every call except the bind carries the
 * Firebase ID token as `Authorization: Bearer` — the token IS the identity;
 * no password, no email and no uid ever travel in the body.
 *
 * BE error codes (stable contract, PR #864) are mapped to FE-friendly
 * `kind`s so pages never string-match messages.
 */

import { env } from '@/lib/env';

const AREA_BASE = '/api/public/intermediary-onboarding/area';

// ─── Shared shapes ───────────────────────────────────────────────────────────

export interface AreaDraftState {
  currentStep: string | null;
  completedSteps: string[];
}

export interface AreaRequestSummary {
  publicId: string;
  status: string;
  effectiveStatus: string;
  draftRevision: number;
  lastSavedAt: string | null;
  submittedAt: string | null;
  cooldownUntil: string | null;
}

/**
 * PR-ONB-PREAUTH-4 — provider-agnostic identity-verification projection.
 * Non-null only while the request is PREAUTHORIZED_FOR_IDENTITY. No raw provider
 * payload, documents or biometrics.
 */
export interface AreaIdentityVerification {
  normalizedStatus: string;
  canStart: boolean;
  canResume: boolean;
  startedAt: string | null;
  completedAt: string | null;
}

export interface AreaMe {
  account: {
    id: string;
    email: string;
    emailVerified: boolean;
    status: 'ACTIVE' | 'CONVERTED' | 'EXPIRED' | 'DISABLED';
    createdAt: string;
  };
  request: AreaRequestSummary | null;
  /** PR-ONB-PREAUTH-4 — identity-verification projection (PREAUTHORIZED only). */
  identityVerification: AreaIdentityVerification | null;
  /** PR-ONB-PRECHECK-3 — version tag the submit must echo back. */
  acceptanceActiveVersion: string;
}

/** PR-ONB-PRECHECK-3 — OPEN correction issue raised by the backoffice. */
export interface AreaOpenIssue {
  id: string;
  targetType: 'FIELD' | 'DOCUMENT';
  targetKey: string;
  reasonCode:
    | 'MISSING_VALUE'
    | 'INVALID_VALUE'
    | 'EXPIRED_DOCUMENT'
    | 'UNREADABLE_DOCUMENT'
    | 'WRONG_DOCUMENT'
    | 'INCONSISTENT_DATA'
    | 'OTHER';
  note: string | null;
  createdAt: string;
}

export interface AreaRequestView extends AreaRequestSummary {
  draftPayload: Record<string, unknown> | null;
  draftState: AreaDraftState | null;
  decisionReason: string | null;
  /** PR-ONB-PRECHECK-3 — populated on NEEDS_CORRECTION. */
  openIssues: AreaOpenIssue[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Failure vocabulary shared by every area call. `AUTH_INVALID` covers 401
 * (missing/expired/garbage token — caller refreshes the token or routes to
 * login); `RESUMABLE_DISABLED` is the BE 503 kill-switch (maintenance UX).
 */
export type AreaFailureKind =
  | 'AUTH_INVALID'
  | 'ACCOUNT_NOT_FOUND'
  | 'ACCOUNT_CONVERTED'
  | 'ACCOUNT_DISABLED'
  | 'ACCOUNT_EXISTS'
  | 'EMAIL_ALREADY_IN_USE'
  | 'COOLDOWN_ACTIVE'
  | 'TURNSTILE_INVALID'
  | 'DRAFT_CONFLICT'
  | 'NOT_EDITABLE'
  | 'NOT_FOUND'
  | 'DOC_TYPE_NOT_ALLOWED'
  | 'DOC_UPLOAD_NOT_ALLOWED'
  | 'DOC_NOT_UPLOADED'
  | 'PAYLOAD_TOO_LARGE'
  | 'RATE_LIMITED'
  | 'RESUMABLE_DISABLED'
  // PR-ONB-PRECHECK-3 — submit/targeted-edit failures.
  | 'EMAIL_NOT_VERIFIED'
  | 'EMAIL_MISMATCH'
  | 'SUBMIT_VALIDATION_FAILED'
  | 'DOCUMENTS_INCOMPLETE'
  | 'CORRECTIONS_INCOMPLETE'
  | 'ACCEPTANCE_VERSION_MISMATCH'
  | 'FIELD_NOT_REOPENED'
  | 'DUPLICATE_ACTIVE_REQUEST'
  | 'RUI_CHECK_FAILED'
  // PR-ONB-PREAUTH-4 — identity-verification start failures.
  | 'NOT_PREAUTHORIZED'
  | 'PROVIDER_UNAVAILABLE'
  | 'NETWORK'
  | 'UNKNOWN';

export interface AreaFailure {
  ok: false;
  kind: AreaFailureKind;
  /** Present on COOLDOWN_ACTIVE (ISO datetime). */
  cooldownUntil?: string;
  /** Present on DRAFT_CONFLICT — the server's revision to reload. */
  currentRevision?: number;
  /** Present on SUBMIT_VALIDATION_FAILED — dotted paths + messages. */
  validationIssues?: Array<{ path: string; message: string }>;
  /** Present on DOCUMENTS_INCOMPLETE — types still missing. */
  missingDocuments?: Array<{ documentType: string; reason: string }>;
  /** Present on CORRECTIONS_INCOMPLETE — issue targets still to correct. */
  unresolvedIssues?: Array<{ targetType: string; targetKey: string }>;
  /** Present on RUI_CHECK_FAILED — the BE message to surface verbatim. */
  message?: string;
}

async function safeJson<T>(res: Response): Promise<T | null> {
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
  details?: {
    cooldownUntil?: string;
    currentRevision?: number;
    issues?:
      | Array<{ path: string; message: string }>
      | Array<{ targetType: string; targetKey: string }>;
    missing?: Array<{ documentType: string; reason: string }>;
  };
}

function mapFailure(status: number, body: ErrorBody | null): AreaFailure {
  const code = body?.error ?? '';
  // PR-ONB-PREAUTH-4 — the identity provider lifecycle carries a SPECIFIC code on
  // 502/503 that must NOT be mistaken for the resumable-disabled 503 kill-switch
  // (which carries ONBOARDING_RESUMABLE_DISABLED). Check the code first.
  if (
    code === 'IDENTITY_VERIFICATION_PROVIDER_NOT_CONFIGURED' ||
    code === 'IDENTITY_VERIFICATION_PROVIDER_ERROR'
  ) {
    return { ok: false, kind: 'PROVIDER_UNAVAILABLE' };
  }
  if (status === 401) return { ok: false, kind: 'AUTH_INVALID' };
  if (status === 503) return { ok: false, kind: 'RESUMABLE_DISABLED' };
  if (status === 429) return { ok: false, kind: 'RATE_LIMITED' };
  if (status === 413) return { ok: false, kind: 'PAYLOAD_TOO_LARGE' };
  switch (code) {
    case 'ONBOARDING_ACCOUNT_NOT_FOUND':
      return { ok: false, kind: 'ACCOUNT_NOT_FOUND' };
    case 'ONBOARDING_ACCOUNT_CONVERTED':
    case 'ONBOARDING_ACCOUNT_ALREADY_CONVERTED':
      return { ok: false, kind: 'ACCOUNT_CONVERTED' };
    case 'ONBOARDING_ACCOUNT_NOT_ACTIVE':
    case 'ONBOARDING_ACCOUNT_DISABLED':
      return { ok: false, kind: 'ACCOUNT_DISABLED' };
    case 'ONBOARDING_ACCOUNT_EXISTS':
      return { ok: false, kind: 'ACCOUNT_EXISTS' };
    case 'EMAIL_ALREADY_IN_USE':
      return { ok: false, kind: 'EMAIL_ALREADY_IN_USE' };
    case 'ONBOARDING_COOLDOWN_ACTIVE':
      return {
        ok: false,
        kind: 'COOLDOWN_ACTIVE',
        ...(body?.details?.cooldownUntil && {
          cooldownUntil: body.details.cooldownUntil,
        }),
      };
    case 'TURNSTILE_INVALID':
      return { ok: false, kind: 'TURNSTILE_INVALID' };
    case 'DRAFT_REVISION_CONFLICT':
      return {
        ok: false,
        kind: 'DRAFT_CONFLICT',
        ...(typeof body?.details?.currentRevision === 'number' && {
          currentRevision: body.details.currentRevision,
        }),
      };
    case 'ONBOARDING_REQUEST_NOT_EDITABLE':
    case 'ONBOARDING_REQUEST_ALREADY_OPEN':
    case 'ONBOARDING_REQUEST_INVALID_TRANSITION':
      return { ok: false, kind: 'NOT_EDITABLE' };
    // PR-ONB-PREAUTH-4 — start verification before the BO preauthorized it.
    case 'ONBOARDING_REQUEST_NOT_PREAUTHORIZED':
      return { ok: false, kind: 'NOT_PREAUTHORIZED' };
    case 'ONBOARDING_DRAFT_NOT_FOUND':
    case 'ONBOARDING_REQUEST_NOT_FOUND':
    case 'ONBOARDING_DOCUMENT_NOT_FOUND':
      return { ok: false, kind: 'NOT_FOUND' };
    case 'ONBOARDING_DOCUMENT_TYPE_NOT_ALLOWED':
      return { ok: false, kind: 'DOC_TYPE_NOT_ALLOWED' };
    case 'ONBOARDING_DOCUMENT_UPLOAD_NOT_ALLOWED':
      return { ok: false, kind: 'DOC_UPLOAD_NOT_ALLOWED' };
    case 'ONBOARDING_DOCUMENT_NOT_UPLOADED':
      return { ok: false, kind: 'DOC_NOT_UPLOADED' };
    // PR-ONB-PRECHECK-3 — area submit / targeted edit failures.
    case 'ONBOARDING_EMAIL_NOT_VERIFIED':
      return { ok: false, kind: 'EMAIL_NOT_VERIFIED' };
    case 'ONBOARDING_EMAIL_MISMATCH':
      return { ok: false, kind: 'EMAIL_MISMATCH' };
    case 'ONBOARDING_DRAFT_INCOMPLETE':
    case 'ONBOARDING_SUBMIT_VALIDATION_FAILED': {
      // `details.issues` is a union across error codes; here it is the
      // {path,message} shape — narrow before surfacing.
      const raw = body?.details?.issues;
      const validationIssues = Array.isArray(raw)
        ? (raw.filter(
            (i): i is { path: string; message: string } =>
              typeof (i as { path?: unknown }).path === 'string',
          ))
        : undefined;
      return {
        ok: false,
        kind: 'SUBMIT_VALIDATION_FAILED',
        ...(validationIssues && validationIssues.length > 0 && { validationIssues }),
      };
    }
    case 'ONBOARDING_DOCUMENTS_INCOMPLETE':
      return {
        ok: false,
        kind: 'DOCUMENTS_INCOMPLETE',
        ...(body?.details?.missing && { missingDocuments: body.details.missing }),
      };
    case 'ONBOARDING_CORRECTIONS_INCOMPLETE':
      return {
        ok: false,
        kind: 'CORRECTIONS_INCOMPLETE',
        ...(Array.isArray(body?.details?.issues) && {
          unresolvedIssues: body.details.issues as Array<{
            targetType: string;
            targetKey: string;
          }>,
        }),
      };
    case 'ONBOARDING_ACCEPTANCE_VERSION_MISMATCH':
      return { ok: false, kind: 'ACCEPTANCE_VERSION_MISMATCH' };
    case 'ONBOARDING_FIELD_NOT_REOPENED':
      return { ok: false, kind: 'FIELD_NOT_REOPENED' };
    case 'ONBOARDING_DUPLICATE_ACTIVE_REQUEST':
      return { ok: false, kind: 'DUPLICATE_ACTIVE_REQUEST' };
    case 'RUI_SECTION_NUMBER_MISMATCH':
    case 'RUI_NUMBER_INVALID_FORMAT':
    case 'RUI_NOT_FOUND_IN_REGISTRY':
    case 'RUI_REGISTRY_SECTION_MISMATCH':
    case 'RUI_REGISTRY_INOPERATIVE':
    case 'RUI_REGISTRY_NAME_MISMATCH':
    case 'RUI_REGISTRY_SUBJECT_KIND_MISMATCH':
    case 'RESPONSIBLE_RUI_NOT_LINKED_TO_ORG':
      return {
        ok: false,
        kind: 'RUI_CHECK_FAILED',
        ...(body?.message && { message: body.message }),
      };
    default:
      return { ok: false, kind: 'UNKNOWN' };
  }
}

async function areaFetch<T>(
  path: string,
  init: { method: 'GET' | 'POST' | 'PUT'; idToken: string; body?: unknown },
): Promise<{ ok: true; data: T } | AreaFailure> {
  let res: Response;
  try {
    res = await fetch(`${env.VITE_API_BASE_URL}${AREA_BASE}${path}`, {
      method: init.method,
      headers: {
        Authorization: `Bearer ${init.idToken}`,
        ...(init.body !== undefined && { 'Content-Type': 'application/json' }),
      },
      ...(init.body !== undefined && { body: JSON.stringify(init.body) }),
    });
  } catch {
    return { ok: false, kind: 'NETWORK' };
  }
  if (res.ok) {
    const json = await safeJson<{ data: T }>(res);
    if (!json) return { ok: false, kind: 'UNKNOWN' };
    return { ok: true, data: json.data };
  }
  return mapFailure(res.status, await safeJson<ErrorBody>(res));
}

// ─── Calls ───────────────────────────────────────────────────────────────────

export interface RegisterBindResult {
  status: 'REGISTERED';
  requestPublicId: string;
  draftRevision: number;
  resumed: boolean;
}

/** Authenticated bind: Firebase identity (token) → provisional account + DRAFT. */
export async function registerAreaBind(
  idToken: string,
  input: {
    turnstileToken: string;
    draftPayload?: Record<string, unknown> | null;
    draftState?: AreaDraftState | null;
    wizardSessionId?: string;
  },
): Promise<{ ok: true; data: RegisterBindResult } | AreaFailure> {
  return areaFetch<RegisterBindResult>('/register', {
    method: 'POST',
    idToken,
    body: input,
  });
}

export async function getAreaMe(
  idToken: string,
): Promise<{ ok: true; data: AreaMe } | AreaFailure> {
  return areaFetch<AreaMe>('/me', { method: 'GET', idToken });
}

export async function getAreaRequest(
  idToken: string,
): Promise<{ ok: true; data: AreaRequestView } | AreaFailure> {
  return areaFetch<AreaRequestView>('/request', { method: 'GET', idToken });
}

export interface SaveDraftOk {
  draftRevision: number;
  lastSavedAt: string;
}

export async function saveAreaDraft(
  idToken: string,
  input: {
    expectedRevision: number;
    draftPayload?: Record<string, unknown> | null;
    draftState?: AreaDraftState | null;
  },
): Promise<{ ok: true; data: SaveDraftOk } | AreaFailure> {
  return areaFetch<SaveDraftOk>('/request/draft', {
    method: 'PUT',
    idToken,
    body: input,
  });
}

export async function withdrawAreaRequest(
  idToken: string,
  reason?: string,
): Promise<{ ok: true; data: { requestPublicId: string; status: 'WITHDRAWN' } } | AreaFailure> {
  return areaFetch('/request/withdraw', {
    method: 'POST',
    idToken,
    body: reason ? { reason } : {},
  });
}

// ─── Documents (PR-ONB-DOCS-2) ───────────────────────────────────────────────

export type AreaDocumentState =
  | 'MISSING'
  | 'UPLOADED'
  | 'IN_REVIEW'
  | 'NEEDS_CORRECTION'
  | 'VALIDATED'
  | 'REJECTED';

export interface AreaDocumentItem {
  documentType: string;
  required: boolean;
  state: AreaDocumentState;
  uploaded: {
    id: string;
    displayName: string;
    mimeType: string;
    sizeBytes: number;
    uploadedAt: string;
  } | null;
  correction: {
    required: boolean;
    note: string | null;
    reviewedAt: string | null;
  } | null;
  canUpload: boolean;
  versionCount: number;
}

export interface AreaDocumentsView {
  requestPublicId: string;
  requestStatus: string;
  /** False = required set still unknown (org data incomplete in the draft). */
  requirementsResolved: boolean;
  items: AreaDocumentItem[];
}

export async function listAreaDocuments(
  idToken: string,
): Promise<{ ok: true; data: AreaDocumentsView } | AreaFailure> {
  return areaFetch<AreaDocumentsView>('/request/documents', {
    method: 'GET',
    idToken,
  });
}

export interface AreaSignDocumentUploadOk {
  documentId: string;
  signedUrl: string;
  expiresAt: string;
}

export async function signAreaDocumentUpload(
  idToken: string,
  input: {
    documentType: string;
    originalFilename: string;
    mimeType: string;
    sizeBytes: number;
  },
): Promise<{ ok: true; data: AreaSignDocumentUploadOk } | AreaFailure> {
  return areaFetch<AreaSignDocumentUploadOk>('/request/documents/sign-upload', {
    method: 'POST',
    idToken,
    body: input,
  });
}

export async function confirmAreaDocumentUpload(
  idToken: string,
  documentId: string,
): Promise<{ ok: true; data: { documentId: string; status: 'UPLOADED' } } | AreaFailure> {
  return areaFetch(`/request/documents/${documentId}/confirm-upload`, {
    method: 'POST',
    idToken,
    body: {},
  });
}

// ─── Submit (PR-ONB-PRECHECK-3) ──────────────────────────────────────────────

export interface AreaSubmitOk {
  requestPublicId: string;
  status: 'SUBMITTED';
  caseType: string | null;
  draftRevision: number;
  idempotent: boolean;
}

/**
 * Submit the CURRENT request for the preliminary check. The body carries NO
 * candidate payload — the server re-reads the draft. `expectedRevision`
 * should come fresh from `/me` or `/request` after the final flush.
 */
export async function submitAreaRequest(
  idToken: string,
  input: {
    expectedRevision: number;
    acceptanceConfirmed: true;
    acceptanceVersion: string;
  },
): Promise<{ ok: true; data: AreaSubmitOk } | AreaFailure> {
  return areaFetch<AreaSubmitOk>('/request/submit', {
    method: 'POST',
    idToken,
    body: input,
  });
}

export async function createAreaDraft(
  idToken: string,
  input: {
    draftPayload?: Record<string, unknown> | null;
    draftState?: AreaDraftState | null;
  } = {},
): Promise<{ ok: true; data: { requestPublicId: string; draftRevision: number } } | AreaFailure> {
  return areaFetch('/request', { method: 'POST', idToken, body: input });
}

// ─── Identity verification start (PR-ONB-PREAUTH-4) ──────────────────────────

export interface AreaIdentityStartOk {
  /** Provider-agnostic normalized KYC status. */
  status: string;
  requiresKyc: boolean;
  /** Provider redirect URL to navigate to; null when terminal / not required. */
  redirectUrl: string | null;
  sessionId: string | null;
  resumed: boolean;
}

/**
 * Start / resume identity verification for the CURRENT request. Empty body:
 * the request is derived server-side from the authenticated account. The gate
 * requires PREAUTHORIZED_FOR_IDENTITY + a verified email; on success the caller
 * navigates the browser to `redirectUrl` (when non-null).
 */
export async function startAreaIdentityVerification(
  idToken: string,
): Promise<{ ok: true; data: AreaIdentityStartOk } | AreaFailure> {
  return areaFetch<AreaIdentityStartOk>('/request/identity-verification/start', {
    method: 'POST',
    idToken,
    body: {},
  });
}
